import React, { useEffect, useState } from 'react';
import { Button, Card, Container, Modal, ProgressBar } from 'react-bootstrap';
import { Link45deg, Pen, PlusCircle, Trash } from 'react-bootstrap-icons';
import { toast } from 'react-toastify';
import FormGenerator from '../../form-generator/FormGenerator';
import { makeApiRequests } from '../../helpers/api';
import { getObjectForPrefill } from '../../helpers/formHelpers';
import AlertModal from '../AlertModal';
import EntityTable from '../common/EntityTable';
import Loader from '../Loader';
import { manufacturerHeaders, manufacturerAddRows, manufacturerUpdateRows } from './constants';
import ManufacturerModels from './ManufacturerModels';
import { isAdmin } from '../../helpers/roleHelpers';
import { copyToClipboard } from '../../helpers/global';

const PUBLIC_MANUFACTURER_BASE_URL = 'https://mobilehomesvonormy.com/manufacturers';

const manufacturerTableSearchKeys = [
  'name',
  'licenseNumber',
  'factoryShortName',
  'websiteDisplayName',
  'representativeName',
  'phoneNumber',
  'email'
];

const getForm = ({ rows, type = 'Add' }) => {
  const form = {
    forms: [
      {
        name: `${type} Manufacturer`,
        markCompulsoryFields: true,
        hideFormName: true,
        compact: true,
        submit: {
          name: type,
          show: true,
          onSubmit: type === 'Add' ? 'onNewManufacturerFormSubmit' : 'onUpdateManufacturerFormSubmit'
        },
        rows
      }
    ]
  };

  return form;
};

const AddEditModal = ({
  show,
  toBeEditedManufacturer,
  onHide,
  showProgress,
  addForm,
  updateForm
}) => (
  <Modal size="lg" show={show} onHide={onHide} centered backdrop="static">
    <Modal.Header closeButton={!showProgress}>
      <Modal.Title>
        <h6 className="mb-0">
          {toBeEditedManufacturer ? 'Update Manufacturer' : 'Add New Manufacturer'}
        </h6>
      </Modal.Title>
    </Modal.Header>
    <Modal.Body className="overflow-auto px-1">
      <FormGenerator
        formJson={toBeEditedManufacturer ? updateForm : addForm}
        formValues={
          toBeEditedManufacturer ? { 'Update Manufacturer': getObjectForPrefill(toBeEditedManufacturer) } : {}
        }
      />
      {showProgress && (
        <ProgressBar
          now={100}
          animated
          striped
          label={toBeEditedManufacturer ? `Updating Manufacturer...` : `Saving Manufacturer....`}
        />
      )}
    </Modal.Body>
  </Modal>
);

const Manufacturer = ({ refetchChoices }) => {
  const [loading, setLoading] = useState(false);
  const [manufacturers, setManufacturers] = useState([]);
  const [deleteInProgress, setDeleteInProgress] = useState(false);
  const [toBeDeletedManufacturer, setToBeDeletedManufacturer] = useState(null);
  const [manufacturerActionMeta, setManufacturerActionMeta] = useState(null);
  const [manufacturerAddingUpdating, setManufacturerAddingUpdating] = useState(false);
  const [expandedManufacturer, setExpandedManufacturer] = useState(null);
  const fetchManufacturers = async () => {
    setLoading(true);

    const { response, error } = await makeApiRequests({
      endpoint: '/manufacturers/list',
      method: 'POST'
    });

    setLoading(false);

    if (error) {
      toast.error(error);
      return;
    }

    setManufacturers(response);
  };

  useEffect(() => {
    fetchManufacturers();
  }, []);

  const deleteManufacturer = async () => {
    setDeleteInProgress(true);

    const { response, error } = await makeApiRequests({
      endpoint: `/manufacturers/${toBeDeletedManufacturer._id}`,
      method: 'DELETE'
    });

    setDeleteInProgress(false);
    if (error) {
      toast.error(error);
      return;
    }

    manufacturers.splice(
      manufacturers.findIndex(i => i._id === toBeDeletedManufacturer._id),
      1
    );
    setManufacturers([...manufacturers]);
    setToBeDeletedManufacturer(null);
  };

  const onNewManufacturerFormSubmit = async form => {
    setManufacturerAddingUpdating(true);

    const { response, error } = await makeApiRequests({
      endpoint: '/manufacturers',
      requestBody: form
    });

    setManufacturerAddingUpdating(false);
    if (error) {
      toast.error(error);
      return;
    }

    setManufacturers([response, ...manufacturers]);
    setManufacturerActionMeta(null);
  };

  const onUpdateManufacturerFormSubmit = async form => {
    setManufacturerAddingUpdating(true);

    const { response, error } = await makeApiRequests({
      endpoint: `/manufacturers/${manufacturerActionMeta.manufacturer._id}`,
      requestBody: { ...form },
      method: 'PUT'
    });

    setManufacturerAddingUpdating(false);
    if (error) {
      toast.error(error);
      return;
    }

    const manufacturerIndex = manufacturers.findIndex(i => i._id === response._id);
    manufacturers[manufacturerIndex] = { ...manufacturers[manufacturerIndex], ...response };
    setManufacturers([...manufacturers]);
    setManufacturerActionMeta(null);
  };

  const onTableRowClick = manufacturer => {
    setExpandedManufacturer(expandedManufacturer?._id === manufacturer._id ? null : manufacturer);
  };

  window['onNewManufacturerFormSubmit'] = onNewManufacturerFormSubmit;
  window['onUpdateManufacturerFormSubmit'] = onUpdateManufacturerFormSubmit;

  const copyPublicManufacturerLink = manufacturer => {
    const displayName = (manufacturer.websiteDisplayName || '').trim();
    if (!displayName) {
      toast.error('Website Display Name is missing; cannot build the link.');
      return;
    }
    const url = `${PUBLIC_MANUFACTURER_BASE_URL}/${encodeURIComponent(displayName)}`;
    copyToClipboard(url)
      .then(() => toast.success('Manufacturer link copied!'))
      .catch(() => toast.error('Could not copy link.'));
  };

  const actionCell = manufacturer => {
    if (!isAdmin()) return null;

    return (
      <div>
         <Button
          variant="outline-primary"
          className="px-1 py-0 mr-1"
          title="Copy public manufacturer URL"
          onClick={(e) => { e.stopPropagation(); copyPublicManufacturerLink(manufacturer) }}
        >
          <Link45deg size={12} />
        </Button>
        <Button
          variant="outline-primary"
          className="px-1 py-0 mr-1"
          onClick={(e) => { e.stopPropagation(); setManufacturerActionMeta({ manufacturer, mode: 'edit' }) }}
        >
          <Pen size={10} />
        </Button>   
        <Button
          variant="outline-danger"
          className="px-1 py-0"
          onClick={(e) => { e.stopPropagation(); setToBeDeletedManufacturer(manufacturer) }}
        >
          <Trash size={10} />
        </Button>
      </div>
    );
  };

  return (
    <Container fluid className="h-100 py-3 px-md-3">
      <Card>
        <Card.Body>
          <h6 className='mb-0'>Manufacturers</h6>
          <hr className="my-3" />
          {loading ? (
            <Loader />
          ) : (
            <>
              <EntityTable
                rowKeyField={'_id'}
                data={manufacturers}
                headers={manufacturerHeaders}
                searchQueryKeys={manufacturerTableSearchKeys}
                additionalComponents={() =>
                  isAdmin() && (
                    <Button size="sm" variant="success" onClick={() => setManufacturerActionMeta({ mode: 'add' })}>
                      <PlusCircle className="mr-1" /> New Manufacturer
                    </Button>
                  )
                }
                showActions={isAdmin()}
                actionCell={actionCell}
                expandedRowKeys={expandedManufacturer ? [expandedManufacturer?._id] : []}
                renderExpandedRow={manufacturer => <ManufacturerModels manufacturer={manufacturer} refetchChoices={refetchChoices} />}
                onRowClick={onTableRowClick}
              />
              {manufacturers.length > 0 && (
                <AlertModal
                  show={toBeDeletedManufacturer !== null}
                  alertText={`Are you sure you want to delete this manufacturer? This action cannot be undone!`}
                  onHide={() => setToBeDeletedManufacturer(null)}
                  onDismissClick={() => setToBeDeletedManufacturer(null)}
                  onContinueClick={deleteManufacturer}
                  progressText={`Deleting manufacturer...`}
                  showProgress={deleteInProgress}
                />
              )}
              <AddEditModal
                show={manufacturerActionMeta !== null}
                onHide={() => setManufacturerActionMeta(null)}
                toBeEditedManufacturer={manufacturerActionMeta ? manufacturerActionMeta.manufacturer : null}
                showProgress={manufacturerAddingUpdating}
                addForm={getForm({ rows: manufacturerAddRows })}
                updateForm={getForm({
                  rows: manufacturerUpdateRows,
                  type: 'Update'
                })}
              />
            </>
          )}
        </Card.Body>
      </Card>
    </Container>
  );
};

export default Manufacturer;
